import { useState, useCallback, useRef, useEffect } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ActionMap<T extends Record<string, any>> = {
  [K in keyof T]: {
    type: K;
    data: T[K];
  };
}[keyof T];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UseActionReturn<T extends Record<string, any>> = {
  action: ActionMap<T> | undefined;
  setAction: (
    currentActionTypes: (keyof T)[] | keyof T | undefined,
    nextAction: ActionMap<T> | undefined,
  ) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SetAction<T extends Record<string, any>> = (
  currentActionTypes: (keyof T)[] | keyof T | undefined,
  nextAction: ActionMap<T> | undefined,
) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useAction = <T extends Record<string, any>>(): UseActionReturn<
  T
> => {
  const [action, setActionState] = useState<ActionMap<T> | undefined>(
    undefined,
  );

  const setAction = useCallback(
    (
      currentActionTypes: (keyof T)[] | keyof T | undefined,
      nextAction?: ActionMap<T>,
    ) => {
      setActionState((prevAction) => {
        // Log what we're transitioning from and to
        const fromAction = prevAction?.type ? String(prevAction.type) : "undefined";
        const toAction = nextAction?.type ? String(nextAction.type) : "undefined";
        console.log(
          `[setAction] Transitioning from "${fromAction}" to "${toAction}"`,
        );

        let shouldTransition = false;

        if (Array.isArray(currentActionTypes)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (currentActionTypes.includes(undefined as any) && !prevAction) {
            shouldTransition = true;
          }
          if (prevAction && currentActionTypes.includes(prevAction.type)) {
            shouldTransition = true;
          }
        } else {
          if (currentActionTypes === undefined && !prevAction) {
            shouldTransition = true;
          }
          if (prevAction && prevAction.type === currentActionTypes) {
            shouldTransition = true;
          }
        }

        if (!shouldTransition) {
          const expectedTypes = Array.isArray(currentActionTypes)
            ? currentActionTypes.map(t => String(t)).join(", ")
            : String(currentActionTypes);
          console.warn(
            `[setAction] Mismatch: Expected current action to be one of [${expectedTypes}], but current action is "${fromAction}". Transition blocked.`,
          );
          return prevAction;
        }

        return nextAction;
      });
    },
    [],
  );

  return {
    action,
    setAction,
  };
};

export function useActionData<
  A extends { type: PropertyKey; data: unknown },
  T extends A["type"],
>(
  action: A | undefined,
  actionType: T,
): Extract<A, { type: T }>["data"] | undefined {
  return action?.type === actionType
    ? (action as Extract<A, { type: T }>).data
    : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useActionTransition = <T extends Record<string, any>>(
  action: ActionMap<T> | undefined,
  fromState: keyof T | undefined,
  toState: keyof T | undefined,
  callback: () => void,
) => {
  const prevActionRef = useRef<ActionMap<T> | undefined>(action);

  useEffect(() => {
    const prevActionType = prevActionRef.current?.type;
    const currentActionType = action?.type;

    // Check if we're transitioning from the expected state to the expected state
    if (prevActionType === fromState && currentActionType === toState) {
      callback();
    }

    // Update previous action reference
    prevActionRef.current = action;
  }, [action, fromState, toState, callback]);
};
